import { A, useNavigate } from "@solidjs/router";
import { BiRegularMenu, BiSolidUser } from "solid-icons/bi";
import {
  IoArrowForward,
  IoBusinessOutline,
  IoHelpCircle,
  IoSettingsOutline,
} from "solid-icons/io";
import { type ParentComponent, Show } from "solid-js";
import { produce } from "solid-js/store";
import { removeAuthToken } from "@/api/client";
import { useI18n } from "@/api/i18n";
import compactLogo from "@/assets/compact-light-logo.svg";
import extendedLogo from "@/assets/extended-light-logo.svg";
import {
  LayoutProvider,
  useLayoutContext,
} from "@/components/layout/context/LayoutContext";
import { Container } from "@/components/shared/Container";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SupportModal } from "@/domains/business/support/components/SupportModal";
import {
  useCompanyActions,
  useCompanyStore,
} from "@/domains/company/company/store/company-store";
import { AvailabilityModal } from "@/domains/freelance/availability/components/AvailabilityModal";
import { isAvailableNow } from "@/domains/freelance/availability/utils/is-available-now";
import { clearLegacyJobOfferDrafts } from "@/domains/job-offer/create/utils/legacy-drafts";
import { usePostHog } from "@/hooks/usePostHog";
import { useAuthV2 } from "../context/auth-provider";

export const Layout: ParentComponent = (props) => {
  const navigate = useNavigate();
  const [authStoreV2, setAuthStoreV2] = useAuthV2();
  const [companyStore] = useCompanyStore();
  const { clearCompany } = useCompanyActions();
  const { setIsSupportModalOpen, openAvailabilityModal } = useLayoutContext();
  const { reset } = usePostHog();

  const logout = async () => {
    if (setAuthStoreV2)
      setAuthStoreV2(
        produce((state) => {
          state.profile = null;
          state.companies = null;
          state.user = null;
          state.user_applications = null;
        }),
      );
    removeAuthToken();
    await clearCompany();
    clearLegacyJobOfferDrafts();
    reset();
    // Through /auth/logout, not straight to /auth: that route is the single
    // teardown path, and it is what wipes the company onboarding draft
    // (company name, VAT number, address, phone — PLAN D15). This layout
    // renders its Logout button exactly when no company is selected, i.e. for
    // the users most likely to be holding such a draft. Copying the wipe here
    // would make it the sixth hand-maintained teardown site; routing keeps one.
    // The destination replaces this entry (`<Navigate>` uses replace), so the
    // history ends up identical to navigating to /auth directly.
    navigate("/auth/logout");
  };

  const { t } = useI18n();
  const canPublish = () => authStoreV2?.capabilities?.can_publish === true;

  return (
    <>
      <header class="fixed top-0 left-0 right-0 z-50">
        <Show when={!authStoreV2?.profile?.id}>
          <div class="bg-matisse-100 h-10 w-full flex items-center justify-center p-2 md:p-0">
            <div class="flex items-center justify-center">
              <p class="lg:text-base text-sm">
                {t("layout.banner.title")}{" "}
                <span class="font-bold text-primary">
                  {t("layout.banner.title_2")}{" "}
                </span>
                {t("layout.banner.description")}{" "}
                <span class="font-bold text-primary">
                  {t("layout.banner.description_2")}{" "}
                </span>
                <span class="md:hidden inline-flex items-center gap-1">
                  <span class="text-sm"> |</span>
                  <A
                    href="/auth"
                    class="underline text-primary hover:text-primary/80"
                  >
                    Accedi
                  </A>{" "}
                  <IoArrowForward class="w-4 h-4" />
                </span>
              </p>
              <div class="ml-2 border-l border-matisse-500 pl-2 text-sm hover:underline items-center gap-1 hidden md:flex">
                <A href="/auth">Accedi</A> <IoArrowForward class="w-4 h-4" />
              </div>
            </div>
          </div>
        </Show>
        <nav class="bg-white shadow-md">
          <Container size="6xl" class="flex items-center justify-between">
            <div class="flex items-center gap-4">
              <A
                href={
                  authStoreV2?.capabilities?.can_publish
                    ? "/profile/company"
                    : "/"
                }
              >
                <img
                  src={compactLogo}
                  alt="Sevedemo logo"
                  class="w-auto h-8 block md:hidden"
                />
                <img
                  src={extendedLogo}
                  alt="Sevedemo logo"
                  class="w-auto h-6 hidden md:block"
                />
              </A>
            </div>
            <div class="flex items-center justify-end h-14 gap-2">
              <Show
                when={canPublish() && companyStore?.selectedCompany}
                fallback={
                  <Button
                    as={A}
                    href="/job-offers/create/start"
                    size="sm"
                    variant="default"
                  >
                    {t("layout.nav.postJobOffer")}
                  </Button>
                }
              >
                <Button
                  as={A}
                  href="/job-offers/create/ai"
                  size="sm"
                  variant="default"
                >
                  {t("layout.nav.postJobOffer")}
                </Button>
              </Show>

              <Show when={authStoreV2?.profile?.weekly_hours}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openAvailabilityModal("navigation_bar")}
                >
                  <Show
                    when={isAvailableNow(authStoreV2?.profile?.available_from)}
                    fallback="Aggiungi Disponibilità"
                  >
                    {authStoreV2?.profile?.weekly_hours ?? "40"}{" "}
                    {t("availability.h_weekly_hours")}
                  </Show>
                </Button>
              </Show>
              <Show
                when={authStoreV2?.profile?.id}
                fallback={
                  <Button as={A} href="/auth" size="sm">
                    {t("common.login")}
                  </Button>
                }
              >
                <Show
                  when={companyStore?.selectedCompany}
                  fallback={
                    <>
                      <Button
                        as={A}
                        href="/profile"
                        size="sm"
                        variant="outline"
                        aria-label={t("layout.common.profile")}
                      >
                        <BiSolidUser class="w-6 h-6" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsSupportModalOpen(true)}
                        aria-label={t("layout.common.contactSupport")}
                      >
                        <IoHelpCircle class="w-5 h-5" />
                      </Button>

                      <Button size="sm" onClick={logout} variant="outline">
                        {t("common.logout")}
                      </Button>
                    </>
                  }
                >
                  <PopoverProfileMenu logout={logout} />
                </Show>
              </Show>
            </div>
          </Container>
        </nav>
      </header>
      <div>{props.children}</div>

      {/* Support Modal */}
      <SupportModal />

      {/* Availability Modal */}
      <AvailabilityModal />
    </>
  );
};
export const RootLayout: ParentComponent = (props) => {
  return (
    <LayoutProvider>
      <Layout>{props.children}</Layout>
    </LayoutProvider>
  );
};

const PopoverProfileMenu = (props: { logout: () => void }) => {
  const [companyStore] = useCompanyStore();
  const { t } = useI18n();
  return (
    <Popover>
      <PopoverTrigger
        as={Button}
        variant="outline"
        aria-label={t("layout.common.openMenu")}
      >
        <BiRegularMenu class="w-6 h-6" />
      </PopoverTrigger>
      <PopoverContent hideCloseButton class="max-w-48">
        <div class="flex flex-col gap-2">
          {companyStore?.selectedCompany?.kind !== "company" && (
            <A href="/profile">
              <div class="flex items-center gap-2 p-2 rounded-md hover:bg-primary/10 cursor-pointer hover:text-primary">
                <BiSolidUser class="w-6 h-6" />
                <p>{t("layout.nav.profile")}</p>
              </div>
            </A>
          )}
          {companyStore?.selectedCompany && (
            <>
              <A href="/profile/company">
                <div class="flex items-center gap-2 p-2 rounded-md hover:bg-primary/10 cursor-pointer hover:text-primary">
                  <IoBusinessOutline class="w-6 h-6" />
                  <p>{t("layout.nav.company")}</p>
                </div>
              </A>
              <A href="/profile/company/ats">
                <div class="flex items-center gap-2 p-2 rounded-md hover:bg-primary/10 cursor-pointer hover:text-primary">
                  <IoSettingsOutline class="w-6 h-6" />
                  <p>ATS</p>
                </div>
              </A>

              <Button onClick={props.logout} variant="outline">
                {t("common.logout")}
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
